import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,  
  Typography
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CircleIcon from "@mui/icons-material/Circle";
import Stack from "@mui/material/Stack";

import { getJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import ViewTermiteLocationCard from "./FormComponents/ViewTermiteLocationCard";
import ViewExterraCard from "./FormComponents/ViewExterraCard";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import TechnicianOnlyReviewSection from "./FormComponents/TechnicianOnlyReviewSection";
import ClientOnlyReviewSection from "./FormComponents/ClientOnlyReviewSection";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import SectionTimestampNotice from "./FormComponents/SectionTimestampNotice";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";
import { getEffectiveEndTime } from "./utils/timeFormatter";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function ViewTermiteControlForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [error] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage(`Error fetching job order: ${err?.message || err}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id]);

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  const effectiveEndTime = getEffectiveEndTime(jobOrder);

  const displayJobOrder = {
    ...jobOrder,
    end_time: effectiveEndTime,
  };
  
  const pageOneJobOrder = {
    ...displayJobOrder,
    client: displayJobOrder?.client || client || {},
  };

  const STRUCTURE_TYPE_OPTIONS = [
    { key: "residential", label: "Residential" },
    { key: "commercial",  label: "Commercial"  },
    { key: "industrial",  label: "Industrial"  },
  ];

  // Normalize truthy values (true / "true" / 1 / "yes", etc.)
  const isTrue = (v) =>
    typeof v === "boolean" ? v
    : typeof v === "number" ? v === 1
    : typeof v === "string" ? ["true","1","yes","y","on"].includes(v.trim().toLowerCase())
    : false;

  // Compute only the selected types, in display order
  const displayedStructureTypes = STRUCTURE_TYPE_OPTIONS
    .filter(opt => isTrue(jobOrder?.structure_type?.[opt.key]))
    .map(opt => opt.label);

  // helper keeps zeros, and prints — for empty/undefined
  const displayValue = (v) => {
    if (v === 0 || v === "0") return "0";
    if (v == null) return "—";
    const s = String(v).trim();
    return s.length ? s : "—";
  };

  const sd = jobOrder?.structure_details ?? {};

  const normalize = (v) => String(v ?? "").trim().toLowerCase();

  const ta = normalize(jobOrder?.termite_activity);

  // helper for termite recommendations
  const cleanList = (arr) =>
  (arr ?? [])
    .map((s) => String(s ?? "").trim())
    .filter(Boolean); // remove empty strings/nulls

  // inside your component (above return)
  const termiteRecommendations = cleanList(jobOrder?.termite_recommendations);

  const STEP_LABELS = {
    review_job_details: "Review Job Details Completed At",
    inspection_report: "Inspection Report Completed At",
    service_details: "Service Details Completed At",
    payment_details: "Payment Details Completed At",
    technician_confirmation: "For Technicians Only Completed At",
    client_signature: "For Client Only - Signature Completed At",
    client_satisfaction: "For Client Only - Satisfaction Completed At",
  };

  const getLatestStepLogMap = (logs = []) => {
    return (Array.isArray(logs) ? logs : []).reduce((acc, log) => {
      const stepKey = log?.step_key;
      const timestamp = log?.timestamp;

      if (!stepKey || !timestamp) return acc;

      const prev = acc[stepKey];
      if (!prev || new Date(timestamp) > new Date(prev)) {
        acc[stepKey] = timestamp;
      }

      return acc;
    }, {});
  };

  const latestStepLogMap = getLatestStepLogMap(jobOrder?.report_page_logs);

  const getStepTimestamp = (stepKey) => {
    return (
      jobOrder?.[`${stepKey}_completed_at`] ||
      latestStepLogMap?.[stepKey] ||
      ""
    );
  };

  const documentLinks = {
    ...buildGeneratedDocumentLinks(jobOrder?.id, {
      includeReport: false,
      includeCertificate: false,
      includeReceipt: true,
    }),
    ...(jobOrder?.receipts_url && {
      receipts_url: jobOrder.receipts_url,
    }),
  };
  
  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <PageOneReviewSection
          jobOrder={pageOneJobOrder}
          client={client}
          selectedJobTypes={displayedJobTypes}
          selectedJobDescriptions={displayedJobDescriptions}
          jobOrderParameters={{ status: jobOrder?.status || "" }}
          errors={{}}
          onStatusChange={() => {}}
          showStatus={true}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.review_job_details}
              value={getStepTimestamp("review_job_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Structure Type Section (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Structure Type</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                {displayedStructureTypes.length > 0 ? (
                  displayedStructureTypes.map((label, idx) => (
                    <Box
                      key={idx}
                      component="li"
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: "0.5rem" }} />
                      <Typography variant="body2">{label}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None selected
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Structure Details (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Structure Details
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Bedrooms
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.bedrooms)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Restrooms
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.restrooms)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Floors
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.floors)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Termite Activity (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Termite Activity
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{
                    width: "100%",
                    opacity: ta === "with" ? 1 : 0.75,
                  }}
                  control={
                    <Checkbox
                      checked={ta === "with"}
                      disabled
                    />
                  }
                  label="With Active Termites"
                />

                <FormControlLabel
                  sx={{
                    width: "100%",
                    opacity: ta === "without" ? 1 : 0.75,
                  }}
                  control={
                    <Checkbox
                      checked={ta === "without"}
                      disabled
                    />
                  }
                  label="Without Active Termites"
                />
              </FormGroup>

              {ta !== "with" && ta !== "without" && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No selection
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Inspection & Pest Count (Mobile) — view-only, wrapped in a parent card */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Inspection Report
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {(() => {
                const list = (jobOrder?.inspection_report || []).filter((r) => {
                  const keys = [
                    "location",
                    "location_other",
                    "findings",
                    "action_taken",
                    "bait_monitor_location",
                    "remarks",
                  ];
                  return keys.some((k) => (r?.[k] ?? "").toString().trim() !== "");
                });

                return list.length ? (
                  <Stack spacing={2}>
                    {list.map((item, idx) => (
                      <ViewTermiteLocationCard key={idx} item={item} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No inspection items
                  </Typography>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.inspection_report}
              value={getStepTimestamp("inspection_report")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Recommendations Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Recommendations
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {termiteRecommendations.length ? (
                <Box component="ul" sx={{ m: 0, pl: 3, listStyle: "disc" }}>
                  {termiteRecommendations.map((label, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">None selected</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Exterra Section */}
        <Grid item xs={12}>
          <ViewExterraCard stations={jobOrder?.exterra_bait_stations} />
        </Grid>

        {/* Chemical Used Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Chemical Used / Batch # / Expiration
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: "bold" }}
                        >
                          Dilution
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment Used
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(jobOrder.chemical_used || []).map((chem, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{chem.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.chemical || ""}
                              disabled
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.dilution || ""}
                              disabled
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          {jobOrder.equipment_used?.length > idx ? (
                            <>
                              <TableCell>
                                {jobOrder.equipment_used[idx].label}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={jobOrder.equipment_used[idx].usage}
                                  disabled
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell />
                              <TableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Notes Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Reminder Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Reminder Notes"
                value={jobOrder.reminder_notes || ""}
                fullWidth
                multiline
                rows={6}
                size="small"
                disabled
              />
            </CardContent>
          </Card>
        </Grid>

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.service_details}
              value={getStepTimestamp("service_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Payment Section */}
        <PaymentDetailsSection
          serviceFee={jobOrder.service_fee}
          paymentStatusOptions={jobOrder.service_report_payment_status || []}
          paymentMethodOptions={jobOrder.service_report_payment_method || []}
          readOnly={true}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.payment_details}
              value={getStepTimestamp("payment_details")}
              colorSet="info"
            />
          </Grid>
        )}

        {/* Technician Only Section */}
        <TechnicianOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label={STEP_LABELS.technician_confirmation}
              value={getStepTimestamp("technician_confirmation")}
              colorSet="info"
            />
          </Grid>
        )}
      
        {/* Client Only Section */}
        <ClientOnlyReviewSection
          jobOrder={displayJobOrder}
          isTechnician={isTechnician}
        />

        {/* Document Links Section */}
        <DocumentLinksCard
          title="Generated Documents"
          links={documentLinks}
        />

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && (
          <Grid item xs={12}>
            <SectionTimestampNotice
              label="Report Submitted At"
              value={jobOrder?.report_submitted_at}
              colorSet="warning"
            />
          </Grid>
        )}

        {/* Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewTermiteControlForm;
